import Console from '~/utils/lib/core/console'
import { Maybe, clone, not, test } from '~/utils/lib/core/prelude'
import { DEFAULT_ANONYMOUS_ROUTE, DEFAULT_USER_ROUTE } from '~/utils/lib/layout/types'
import { findCompany, findStructure, useStore_client_access } from '~/stores/client/access/index'
import { useStore_core_referer } from '~/stores/core/referer'
import { getMainRoute as dashboard_structure_route } from '~/stores/dashboard'
import { getLastSelectedCompany, getLastSelectedStructure } from '~/stores/layout/navigation'

const console = new Console('plugin:redirection')
/**
 * @desc Check whether the internaut is on an authentication route
 * @param {import('~/utils/lib/core/types_').Route_} route
 * @return {boolean}
 */
export function isOnAuthenticationPages(route) {
  return test(/^\/account\//, route.path)
}
/**
 * @desc Return the eventual referer snapshot, and if so flush it
 * @NOTE since "nuxt@4.2" can not reuse the top-level package "vue-router",
 *       hence the odd typing
 * @returns {import('~/utils/lib/core/prelude_').Maybe<import('~/utils/lib/core/types_').Route_>}
 */
export function popRefererSnapshot() {
  const referer = useStore_core_referer()
  const hasRefererSnapshot = referer.isSnapshotMade

  if (not(hasRefererSnapshot))
    return Maybe.Nothing()

  const snapshot = clone(referer.route)

  // flush referer now
  referer.$reset()

  // @NOTE prevent any business regarding "account" pages (as it might leads
  //       to infined loop or unwanted behaviors) (KISS decision)
  if (isOnAuthenticationPages(snapshot))
    return Maybe.Nothing()

  console.log(
    'found referer snapshot on',
    snapshot.path
  )

  return Maybe.Just(snapshot)
}

/**
 * @desc Return a route path for a user according to its
 *    previous stored selection
 *
 *    (!) do not use `navigateTo` here directly, as "nuxt4" + TypeScript
 *        transpilation will sometimes throws error (according to the scope)
 *
 * @promise-never-rejects
 * @returns {Promise<import('vue-router').RouteLocationNormalizedGeneric["path"]>}
 */
export async function routeForUser() {
  const accessData = useStore_client_access().data

  const mStructureId  = await getLastSelectedStructure()
  const mCompanyCode  = await getLastSelectedCompany()

  switch (true) {

    case Maybe.isJust(mStructureId)
      && Maybe.isJust(mCompanyCode):
      {
        const structureId = Maybe.fromJust(mStructureId)
        const companyCode = Maybe.fromJust(mCompanyCode)

        const mCompany
          = findStructure(accessData, structureId)
          .chain(structure => findCompany(structure, companyCode))

        if (Maybe.isNothing(mCompany)) {
          console.log(
              `returning last viewed company failed`,
              `(no access granted),`,
              `redirect on default user route`
            )

          return DEFAULT_USER_ROUTE
        }

        const mStructureACLs = findStructure(accessData, structureId)
          .map(structure => structure.acl)

        if (Maybe.isNothing(mStructureACLs)) {
          console.error(
              `last viewed structure="${structureId}`,
              `not bearing any ACLs,`,
              `this SHOULD NOT happen,`,
              `redirect on default user route`
            )

          return DEFAULT_USER_ROUTE
        }

        const route = dashboard_structure_route(structureId, companyCode)

        console.log(
          `last viewed company level`,
          `(route="${route}")`
        )

        return route
      }

    case Maybe.isJust(mStructureId):
      {
        const structureId = Maybe.fromJust(mStructureId)
        const mStructureACLs = findStructure(accessData, structureId)

        if (Maybe.isNothing(mStructureACLs)) {
          console.error(
              `last viewed structure="${structureId}`,
              `not bearing any ACLs,`,
              `this SHOULD NOT happen,`,
              `redirect on default user route`
            )

          return DEFAULT_USER_ROUTE
        }

        const route = dashboard_structure_route(structureId)

        console.log(
          `last viewed structure level`,
          `(route="${route}")`
        )

        return route
      }

    default:
      {
        console.log(
          `automatic redirection on user default page`
        )

        return DEFAULT_USER_ROUTE
      }
  }
}
/**
 * @desc Returning the route for an anonymous user
 *    Right now just a reference, as no logic has been set
 *
 *    (!) do not use `navigateTo` here directly, as "nuxt4" + TypeScript
 *        transpilation will sometimes throws error (according to the scope)
 *
 * @returns {string}
 */
export function routeForAnonymous() {
  return DEFAULT_ANONYMOUS_ROUTE
}
